import React, { useMemo } from 'react';
import {
  ArrowPathIcon,
  ChatBubbleLeftEllipsisIcon,
  CheckCircleIcon,
  ClockIcon,
  PhoneIcon,
  TagIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { LeadTimelineEvent } from '../../types/lead';

const TYPE_LABELS: Record<string, string> = {
  status_change: 'Status changed',
  sub_stage_change: 'Sub-stage changed',
  follow_up_added: 'Follow-up scheduled',
  note_added: 'Note added',
  note_updated: 'Note updated',
  note_deleted: 'Note deleted',
  call_completed: 'Call completed',
  agent_assigned: 'Agent assigned',
  agent_tagged: 'Agent tagged',
  agent_untagged: 'Agent untagged',
};

const EVENT_STYLE: Record<
  string,
  { icon: React.ComponentType<{ className?: string }>; dot: string; bg: string }
> = {
  status_change: { icon: ArrowPathIcon, dot: 'bg-blue-500', bg: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  sub_stage_change: { icon: ArrowPathIcon, dot: 'bg-indigo-500', bg: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400' },
  follow_up_added: { icon: ClockIcon, dot: 'bg-amber-500', bg: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  note_added: { icon: ChatBubbleLeftEllipsisIcon, dot: 'bg-emerald-500', bg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  note_updated: { icon: ChatBubbleLeftEllipsisIcon, dot: 'bg-emerald-500', bg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  note_deleted: { icon: ChatBubbleLeftEllipsisIcon, dot: 'bg-red-500', bg: 'bg-red-500/10 text-red-600 dark:text-red-400' },
  call_completed: { icon: PhoneIcon, dot: 'bg-sky-500', bg: 'bg-sky-500/10 text-sky-600 dark:text-sky-400' },
  agent_assigned: { icon: UserIcon, dot: 'bg-violet-500', bg: 'bg-violet-500/10 text-violet-600 dark:text-violet-400' },
  agent_tagged: { icon: TagIcon, dot: 'bg-violet-500', bg: 'bg-violet-500/10 text-violet-600 dark:text-violet-400' },
  agent_untagged: { icon: TagIcon, dot: 'bg-muted-foreground', bg: 'bg-muted/40 text-muted-foreground' },
};

const humanize = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function describeEvent(event: LeadTimelineEvent): string {
  const p = event.payload;
  switch (event.type) {
    case 'status_change':
      return p.from && p.to
        ? `${humanize(String(p.from))} → ${humanize(String(p.to))}`
        : humanize(String(p.to || 'Updated'));
    case 'sub_stage_change':
      return p.subStageName ? String(p.subStageName) : 'Sub-stage updated';
    case 'follow_up_added':
      return p.scheduledAt
        ? new Date(String(p.scheduledAt)).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })
        : 'Follow-up scheduled';
    case 'note_added':
    case 'note_updated':
    case 'note_deleted':
      return p.preview ? String(p.preview) : TYPE_LABELS[event.type] || event.type;
    case 'call_completed':
      return [p.reason, p.status].filter(Boolean).map(String).join(' · ') || 'Call logged';
    case 'agent_assigned':
    case 'agent_tagged':
    case 'agent_untagged':
      return String(p.agentName || p.agentId || 'Updated');
    default:
      return humanize(event.type);
  }
}

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const formatDayLabel = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
};

interface LeadTimelineProps {
  events: LeadTimelineEvent[];
  loading?: boolean;
}

const LeadTimeline: React.FC<LeadTimelineProps> = ({ events, loading }) => {
  const grouped = useMemo(() => {
    const map = new Map<string, LeadTimelineEvent[]>();
    for (const event of events) {
      const key = formatDayLabel(event.createdAt);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(event);
    }
    return Array.from(map.entries());
  }, [events]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-sm text-muted-foreground">
        <ClockIcon className="mb-2 h-8 w-8 animate-pulse opacity-40" />
        Loading activity...
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border py-12 text-center">
        <ClockIcon className="mx-auto mb-2 h-8 w-8 text-muted-foreground/30" />
        <p className="text-sm font-medium text-muted-foreground">No activity yet</p>
        <p className="mt-1 text-xs text-muted-foreground">Calls, notes, and status changes will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {grouped.map(([dayLabel, dayEvents]) => (
        <section key={dayLabel}>
          <div className="sticky top-0 z-10 mb-3 bg-background/95 py-1 backdrop-blur-sm">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{dayLabel}</h4>
          </div>

          <ol className="relative space-y-0">
            {dayEvents.map((event, index) => {
              const style = EVENT_STYLE[event.type] ?? {
                icon: ClockIcon,
                dot: 'bg-muted-foreground',
                bg: 'bg-muted/40 text-muted-foreground',
              };
              const Icon = style.icon;
              const isLast = index === dayEvents.length - 1;

              return (
                <li key={event.id} className="relative flex gap-3 pb-5">
                  {!isLast && (
                    <span
                      className="absolute left-[15px] top-8 bottom-0 w-px bg-border"
                      aria-hidden
                    />
                  )}

                  <span
                    className={`relative z-[1] flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-4 ring-background ${style.bg}`}
                  >
                    <Icon className="h-4 w-4" />
                  </span>

                  <div className="min-w-0 flex-1 rounded-xl border border-border bg-card px-3 py-2.5 shadow-sm">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-foreground">
                        {TYPE_LABELS[event.type] || humanize(event.type)}
                      </p>
                      <time className="shrink-0 text-[11px] text-muted-foreground">
                        {new Date(event.createdAt).toLocaleTimeString(undefined, {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </time>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground break-words">{describeEvent(event)}</p>
                    <p className="mt-1.5 text-[11px] text-muted-foreground">
                      {event.createdByName ? (
                        <>
                          <span className="font-medium text-foreground/80">{event.createdByName}</span>
                          {' · '}
                        </>
                      ) : null}
                      {formatTime(event.createdAt)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
};

export default LeadTimeline;
